/**
 * Native mega menu behaviour.
 *
 * Enhances every <details class="mega-menu"> rendered by
 * snippets/mega-menu.liquid with:
 *  - hover intent (50ms open delay, 100ms close delay — no flicker)
 *  - only one panel open at a time
 *  - aria-expanded kept in sync with the details element
 *  - keyboard support: Escape closes, ArrowDown opens/moves through
 *    links, ArrowUp moves back, ArrowLeft/ArrowRight move between
 *    top-level items, focus leaving the menu closes it
 *
 * Hover handling only activates on fine-pointer desktop viewports;
 * on touch/tablet the native <details> click behaviour is kept.
 */
(() => {
  'use strict';

  const OPEN_DELAY = 50;
  const CLOSE_DELAY = 100;
  const PANEL_GAP = 12; // px between the trigger and the panel

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  const items = [];

  const hoverEnabled = () => desktopQuery.matches && hoverQuery.matches;

  class MegaMenuItem {
    constructor(details) {
      this.details = details;
      this.summary = details.querySelector('summary');
      this.content = details.querySelector('.mega-menu__content');
      this.hoverTarget = details.closest('header-menu') || details;
      this.openTimer = null;
      this.closeTimer = null;

      this.hoverTarget.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.hoverTarget.addEventListener('mouseleave', this.onMouseLeave.bind(this));
      this.details.addEventListener('toggle', this.onToggle.bind(this));
      this.details.addEventListener('keydown', this.onKeydown.bind(this));
      this.details.addEventListener('focusout', this.onFocusOut.bind(this));
    }

    get isOpen() {
      return this.details.hasAttribute('open');
    }

    onMouseEnter() {
      if (!hoverEnabled()) return;
      clearTimeout(this.closeTimer);
      if (this.isOpen) return;
      this.openTimer = setTimeout(() => this.open(), OPEN_DELAY);
    }

    onMouseLeave() {
      if (!hoverEnabled()) return;
      clearTimeout(this.openTimer);
      this.closeTimer = setTimeout(() => this.close(), CLOSE_DELAY);
    }

    onToggle() {
      this.summary.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
      if (this.isOpen) {
        this.positionPanel();
        closeAllExcept(this);
      }
    }

    // Place the panel just below the trigger instead of below the
    // whole header block, whose padding would otherwise become a
    // large dead gap above the dropdown.
    positionPanel() {
      const wrapper = this.details.closest('.header-wrapper');
      if (!wrapper) return;
      const top =
        this.summary.getBoundingClientRect().bottom -
        wrapper.getBoundingClientRect().top +
        PANEL_GAP;
      this.content.style.top = `${Math.round(top)}px`;
    }

    onFocusOut(event) {
      const next = event.relatedTarget;
      if (next && this.hoverTarget.contains(next)) return;
      this.close();
    }

    onKeydown(event) {
      switch (event.key) {
        case 'Escape':
          if (!this.isOpen) return;
          event.preventDefault();
          this.close();
          this.summary.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!this.isOpen) {
            this.open();
            requestAnimationFrame(() => this.focusLink(0));
          } else {
            this.moveFocus(1);
          }
          break;
        case 'ArrowUp':
          if (!this.isOpen) return;
          event.preventDefault();
          this.moveFocus(-1);
          break;
        case 'ArrowLeft':
          if (event.target !== this.summary) return;
          event.preventDefault();
          focusSiblingTopLevel(this.summary, -1);
          break;
        case 'ArrowRight':
          if (event.target !== this.summary) return;
          event.preventDefault();
          focusSiblingTopLevel(this.summary, 1);
          break;
      }
    }

    open() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!this.isOpen) {
        this.positionPanel();
        this.details.setAttribute('open', '');
      }
    }

    close() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.isOpen) this.details.removeAttribute('open');
    }

    links() {
      return Array.from(this.content.querySelectorAll('a[href]'));
    }

    focusLink(index) {
      const links = this.links();
      if (links[index]) links[index].focus();
    }

    moveFocus(delta) {
      const links = this.links();
      const current = links.indexOf(document.activeElement);
      if (current === -1) {
        this.focusLink(delta > 0 ? 0 : links.length - 1);
        return;
      }
      const next = Math.min(Math.max(current + delta, 0), links.length - 1);
      links[next].focus();
    }
  }

  function closeAllExcept(except) {
    items.forEach((item) => {
      if (item !== except) item.close();
    });
  }

  function focusSiblingTopLevel(fromElement, delta) {
    const topLevel = Array.from(
      document.querySelectorAll(
        '.header__inline-menu li > a.header__menu-item, .header__inline-menu summary.header__menu-item'
      )
    );
    const current = topLevel.indexOf(fromElement);
    if (current === -1) return;
    const next = topLevel[current + delta];
    if (next) next.focus();
  }

  function init() {
    document
      .querySelectorAll('details.mega-menu')
      .forEach((details) => items.push(new MegaMenuItem(details)));

    // Close everything when the viewport crosses the desktop
    // breakpoint (rotation, window resize).
    const onChange = () => closeAllExcept(null);
    if (typeof desktopQuery.addEventListener === 'function') {
      desktopQuery.addEventListener('change', onChange);
    } else if (typeof desktopQuery.addListener === 'function') {
      desktopQuery.addListener(onChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
